import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import {
  Search, Plus, Edit, Trash2, ShieldCheck, ShieldOff, Eye, KeyRound, X,
  Ban, CheckCircle2, AlertTriangle, Power, Store, Phone, Mail, MapPin, IndianRupee, Package, Star, RefreshCw, Filter,
} from "lucide-react";
import ImageUploader from "@/components/upload/ImageUploader";

// ---------- Helpers ----------
const STATUS_BADGE = {
  active: "bg-green-100 text-green-700",
  inactive: "bg-muted text-muted-foreground",
  suspended: "bg-red-100 text-red-700",
};
const VERIFY_BADGE = {
  true: "bg-green-100 text-green-700",
  false: "bg-amber-100 text-amber-700",
};

const formatDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" }); } catch { return s; }
};

// ---------- Main Module ----------
const SellerManagement = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("all"); // all|verified|pending
  const [statusFilter, setStatusFilter] = useState("all");      // all|active|inactive|suspended

  const [modal, setModal] = useState(null); // {mode:'add'|'edit'|'view'|'delete'|'reset', seller?}

  const fetchList = async (override = {}) => {
    setLoading(true);
    try {
      const _q = override.q !== undefined ? override.q : q;
      const _state = override.state !== undefined ? override.state : stateFilter;
      const _district = override.district !== undefined ? override.district : districtFilter;
      const _ver = override.verified !== undefined ? override.verified : verifiedFilter;
      const _stat = override.status !== undefined ? override.status : statusFilter;
      const params = new URLSearchParams();
      if (_q) params.set("q", _q);
      if (_state) params.set("state", _state);
      if (_district) params.set("district", _district);
      if (_ver === "verified") params.set("verified", "true");
      if (_ver === "pending") params.set("verified", "false");
      if (_stat !== "all") params.set("status", _stat);
      const { data } = await api.get(`/admin/sellers?${params.toString()}`);
      setSellers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load sellers");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [verifiedFilter, statusFilter]);

  const clearFilters = () => {
    setQ(""); setStateFilter(""); setDistrictFilter(""); setVerifiedFilter("all"); setStatusFilter("all");
    fetchList({ q: "", state: "", district: "", verified: "all", status: "all" });
  };

  const onAction = async (s, action) => {
    try {
      if (action === "verify") {
        await api.post(`/admin/sellers/${s.id}/verify?verified=true`);
        toast.success(`${s.shop_name} verified ✓`);
      } else if (action === "reject") {
        await api.post(`/admin/sellers/${s.id}/verify?verified=false`);
        toast.success(`${s.shop_name} verification revoked`);
      } else if (action === "suspend") {
        await api.post(`/admin/sellers/${s.id}/status`, { status: "suspended" });
        toast.success(`${s.shop_name} suspended`);
      } else if (action === "activate") {
        await api.post(`/admin/sellers/${s.id}/status`, { status: "active" });
        toast.success(`${s.shop_name} activated`);
      }
      fetchList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  // KPI counts
  const counts = useMemo(() => {
    const c = { total: sellers.length, verified: 0, pending: 0, active: 0, suspended: 0 };
    for (const s of sellers) {
      if (s.verified) c.verified++; else c.pending++;
      if (s.account_status === "suspended") c.suspended++;
      else c.active++;
    }
    return c;
  }, [sellers]);

  return (
    <div data-testid="admin-seller-management">
      {/* KPI band */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <MiniKpi label="Total Sellers" value={counts.total} color="bg-primary/10 text-primary" />
        <MiniKpi label="Verified" value={counts.verified} color="bg-green-100 text-green-700" />
        <MiniKpi label="Pending" value={counts.pending} color="bg-amber-100 text-amber-700" />
        <MiniKpi label="Suspended" value={counts.suspended} color="bg-red-100 text-red-700" />
      </div>

      {/* Toolbar */}
      <div className="card-fah p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchList()}
              placeholder="Search by shop, owner, phone, email…" className="flex-1 text-sm focus:outline-none bg-transparent"
              data-testid="seller-search-input"
            />
            {q && <button onClick={() => { setQ(""); fetchList({ q: "" }); }} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
          <input value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} placeholder="State" className="text-sm px-3 py-2 border border-border rounded-lg w-28 bg-white" data-testid="seller-state-filter" />
          <input value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} placeholder="District" className="text-sm px-3 py-2 border border-border rounded-lg w-32 bg-white" data-testid="seller-district-filter" />
          <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="seller-verify-filter">
            <option value="all">All Verification</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="seller-status-filter">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <button onClick={fetchList} className="bg-muted text-foreground text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="seller-search-btn">
            <Filter className="w-3.5 h-3.5" /> Apply
          </button>
          <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="seller-clear-btn">
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
          <button onClick={() => setModal({ mode: "add" })} className="bg-secondary text-secondary-foreground text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 ml-auto" data-testid="btn-add-seller">
            <Plus className="w-4 h-4" /> Add Seller
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-fah overflow-x-auto" data-testid="admin-sellers-table">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-muted/40 text-left">
            <tr>
              {["Seller ID", "Shop", "Owner", "Mobile", "Email", "Address", "State / District / Pin", "GST", "License", "Verified", "Status", "Registered", "Action"].map((h) => (
                <th key={h} className="px-3 py-3 text-[11px] uppercase tracking-wide font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="13" className="px-4 py-10 text-center text-muted-foreground">Loading sellers…</td></tr>
            )}
            {!loading && sellers.length === 0 && (
              <tr><td colSpan="13" className="px-4 py-10 text-center text-muted-foreground">No sellers match the current filters.</td></tr>
            )}
            {!loading && sellers.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-muted/20" data-testid={`seller-row-${s.id}`}>
                <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground" title={s.id}>{s.id.slice(0, 8)}…</td>
                <td className="px-3 py-2.5">
                  <button onClick={() => setModal({ mode: "view", seller: s })} className="flex items-center gap-2 group" data-testid={`seller-open-${s.id}`}>
                    {s.shop_logo || s.avatar ? <img src={s.shop_logo || s.avatar} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center"><Store className="w-4 h-4 text-primary" /></div>}
                    <span className="font-semibold group-hover:text-primary">{s.shop_name}</span>
                  </button>
                </td>
                <td className="px-3 py-2.5">{s.owner_name}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{s.phone}</td>
                <td className="px-3 py-2.5 text-xs">{s.email || "-"}</td>
                <td className="px-3 py-2.5 max-w-[200px] truncate" title={s.address}>{s.address}</td>
                <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                  <div>{s.state || "-"}</div>
                  <div className="text-muted-foreground">{(s.district || s.city || "-")} {s.pincode ? `· ${s.pincode}` : ""}</div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px]">{s.gst_number || <span className="text-muted-foreground">-</span>}</td>
                <td className="px-3 py-2.5 font-mono text-[11px]">{s.license_number || <span className="text-muted-foreground">-</span>}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded ${VERIFY_BADGE[s.verified]}`}>{s.verified ? "Verified" : "Pending"}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[11px] font-semibold uppercase px-2 py-1 rounded ${STATUS_BADGE[s.account_status] || STATUS_BADGE.active}`}>{s.account_status || "active"}</span>
                </td>
                <td className="px-3 py-2.5 text-xs whitespace-nowrap">{formatDate(s.created_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <IconBtn title="View profile" onClick={() => setModal({ mode: "view", seller: s })} testid={`seller-view-${s.id}`} color="text-foreground"><Eye className="w-4 h-4" /></IconBtn>
                    <IconBtn title="Edit" onClick={() => setModal({ mode: "edit", seller: s })} testid={`seller-edit-${s.id}`} color="text-primary"><Edit className="w-4 h-4" /></IconBtn>
                    {s.verified
                      ? <IconBtn title="Revoke verification" onClick={() => onAction(s, "reject")} testid={`seller-reject-${s.id}`} color="text-amber-700"><ShieldOff className="w-4 h-4" /></IconBtn>
                      : <IconBtn title="Verify" onClick={() => onAction(s, "verify")} testid={`seller-verify-${s.id}`} color="text-green-700"><ShieldCheck className="w-4 h-4" /></IconBtn>
                    }
                    {s.account_status === "suspended"
                      ? <IconBtn title="Activate" onClick={() => onAction(s, "activate")} testid={`seller-activate-${s.id}`} color="text-green-700"><CheckCircle2 className="w-4 h-4" /></IconBtn>
                      : <IconBtn title="Suspend" onClick={() => onAction(s, "suspend")} testid={`seller-suspend-${s.id}`} color="text-amber-700"><Ban className="w-4 h-4" /></IconBtn>
                    }
                    <IconBtn title="Reset password" onClick={() => setModal({ mode: "reset", seller: s })} testid={`seller-reset-${s.id}`} color="text-foreground"><KeyRound className="w-4 h-4" /></IconBtn>
                    <IconBtn title="Delete" onClick={() => setModal({ mode: "delete", seller: s })} testid={`seller-delete-${s.id}`} color="text-destructive"><Trash2 className="w-4 h-4" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {modal?.mode === "add" && <SellerFormModal onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchList(); }} />}
      {modal?.mode === "edit" && <SellerFormModal seller={modal.seller} onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchList(); }} />}
      {modal?.mode === "view" && <SellerProfileModal seller={modal.seller} onClose={() => setModal(null)} />}
      {modal?.mode === "delete" && <DeleteSellerModal seller={modal.seller} onClose={() => setModal(null)} onDone={() => { setModal(null); fetchList(); }} />}
      {modal?.mode === "reset" && <ResetPasswordModal seller={modal.seller} onClose={() => setModal(null)} />}
    </div>
  );
};

// ---------- Sub-components ----------
const MiniKpi = ({ label, value, color }) => (
  <div className="card-fah p-3" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${color.split(" ").find(c => c.startsWith("text"))}`}>{value}</div>
  </div>
);

const IconBtn = ({ children, onClick, title, testid, color = "" }) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded hover:bg-muted ${color}`} data-testid={testid}>{children}</button>
);

// ---------- Add / Edit Modal ----------
const SellerFormModal = ({ seller, onClose, onSaved }) => {
  const isEdit = !!seller;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    shop_name: seller?.shop_name || "",
    owner_name: seller?.owner_name || "",
    phone: seller?.phone || "",
    email: seller?.email || "",
    password: "",
    address: seller?.address || "",
    state: seller?.state || "",
    district: seller?.district || seller?.city || "",
    pincode: seller?.pincode || "",
    gst_number: seller?.gst_number || "",
    license_number: seller?.license_number || "",
    aadhaar_number: seller?.aadhaar_number || "",
    pan_number: seller?.pan_number || "",
    bank_account_number: seller?.bank_account_number || "",
    bank_ifsc: seller?.bank_ifsc || "",
    bank_holder_name: seller?.bank_holder_name || "",
    shop_logo: seller?.shop_logo || "",
    shop_banner: seller?.shop_banner || "",
    verified: seller?.verified ?? false,
    account_status: seller?.account_status || "active",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isEdit) {
        const payload = { ...form };
        delete payload.password; // never sent through edit
        await api.put(`/admin/sellers/${seller.id}`, payload);
        toast.success("Seller updated");
      } else {
        if (!form.password || form.password.length < 6) {
          throw new Error("Password must be at least 6 characters");
        }
        await api.post("/admin/sellers", form);
        toast.success("Seller created");
      }
      onSaved();
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? `Edit Seller — ${seller.shop_name}` : "Add New Seller"} onClose={onClose} testid={isEdit ? "edit-seller-modal" : "add-seller-modal"} wide>
      <form onSubmit={submit} className="space-y-5">
        {err && <div className="text-sm bg-destructive/10 text-destructive p-3 rounded-lg" data-testid="seller-form-error">{err}</div>}

        <FormSection title="Shop Information">
          <FieldGrid>
            <TextField label="Shop Name *" value={form.shop_name} onChange={set("shop_name")} required testid="seller-shop-name" />
            <TextField label="Owner Name *" value={form.owner_name} onChange={set("owner_name")} required testid="seller-owner-name" />
            <TextField label="Mobile *" value={form.phone} onChange={set("phone")} required testid="seller-phone" placeholder="+91 98765 43210" />
            <TextField label="Email *" type="email" value={form.email} onChange={set("email")} required testid="seller-email" />
            {!isEdit && <TextField label="Password * (min 6)" type="password" value={form.password} onChange={set("password")} required testid="seller-password" />}
          </FieldGrid>
        </FormSection>

        <FormSection title="Address">
          <FieldGrid>
            <TextField label="Address *" value={form.address} onChange={set("address")} required className="sm:col-span-2" testid="seller-address" />
            <TextField label="State *" value={form.state} onChange={set("state")} required testid="seller-state" />
            <TextField label="District *" value={form.district} onChange={set("district")} required testid="seller-district" />
            <TextField label="Pincode *" value={form.pincode} onChange={set("pincode")} required testid="seller-pincode" />
          </FieldGrid>
        </FormSection>

        <FormSection title="Documents (optional)">
          <FieldGrid>
            <TextField label="GST Number" value={form.gst_number} onChange={set("gst_number")} testid="seller-gst" />
            <TextField label="License Number" value={form.license_number} onChange={set("license_number")} testid="seller-license" />
            <TextField label="Aadhaar Number" value={form.aadhaar_number} onChange={set("aadhaar_number")} testid="seller-aadhaar" />
            <TextField label="PAN Number" value={form.pan_number} onChange={set("pan_number")} testid="seller-pan" />
          </FieldGrid>
        </FormSection>

        <FormSection title="Bank Details (optional)">
          <FieldGrid>
            <TextField label="Account Holder Name" value={form.bank_holder_name} onChange={set("bank_holder_name")} testid="seller-bank-holder" />
            <TextField label="Account Number" value={form.bank_account_number} onChange={set("bank_account_number")} testid="seller-bank-acc" />
            <TextField label="IFSC Code" value={form.bank_ifsc} onChange={set("bank_ifsc")} testid="seller-bank-ifsc" />
          </FieldGrid>
        </FormSection>

        <FormSection title="Shop Logo">
          <SingleImageUploader value={form.shop_logo} onChange={(v) => setForm({ ...form, shop_logo: v })} testid="seller-shop-logo" />
        </FormSection>

        <FormSection title="Shop Banner">
          <SingleImageUploader value={form.shop_banner} onChange={(v) => setForm({ ...form, shop_banner: v })} testid="seller-shop-banner" />
        </FormSection>

        {isEdit && (
          <FormSection title="Verification & Status">
            <FieldGrid>
              <label className="flex items-center gap-2 text-sm pt-6">
                <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} data-testid="seller-edit-verified" />
                Mark as Verified
              </label>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account Status</label>
                <select value={form.account_status} onChange={set("account_status")} className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white text-sm" data-testid="seller-edit-status">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </FieldGrid>
          </FormSection>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted" data-testid="seller-form-cancel">Cancel</button>
          <button disabled={busy} type="submit" className="bg-primary hover:bg-primary-hover text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="seller-form-save">
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Seller"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// ---------- Delete Confirmation ----------
const DeleteSellerModal = ({ seller, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const confirmText = "DELETE";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/sellers/${seller.id}`);
      toast.success(`Seller deleted (${data.deleted_products} product${data.deleted_products === 1 ? "" : "s"} removed)`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Delete Seller" onClose={onClose} testid="delete-seller-modal">
      <div className="text-sm space-y-3">
        <div className="bg-destructive/10 text-destructive p-3 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">This action is permanent.</div>
            <div className="mt-1 text-xs">All products listed by <b>{seller.shop_name}</b> will be removed and the seller&apos;s account will be deleted.</div>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Type <b>{confirmText}</b> to confirm</label>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white text-sm" data-testid="delete-confirm-input" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted" data-testid="delete-cancel">Cancel</button>
          <button disabled={busy || typed !== confirmText} onClick={submit} className="bg-destructive text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-40" data-testid="delete-confirm">
            {busy ? "Deleting…" : "Delete Seller"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ---------- Reset Password ----------
const ResetPasswordModal = ({ seller, onClose }) => {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/sellers/${seller.id}/reset-password`, { password: pwd });
      toast.success(`Password reset for ${seller.shop_name}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Reset password — ${seller.shop_name}`} onClose={onClose} testid="reset-password-modal">
      <div className="space-y-3 text-sm">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New Password (min 6)</label>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white" data-testid="reset-pwd-input" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Cancel</button>
          <button disabled={busy} onClick={submit} className="bg-primary text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="reset-pwd-confirm">
            {busy ? "Resetting…" : "Reset Password"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ---------- Seller Profile View ----------
const SellerProfileModal = ({ seller, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/sellers/${seller.id}/detail`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load profile"))
      .finally(() => setLoading(false));
  }, [seller.id]);

  return (
    <ModalShell title={`Seller Profile — ${seller.shop_name}`} onClose={onClose} testid="seller-profile-modal" wide>
      {loading ? (
        <div className="py-10 text-center text-muted-foreground">Loading profile…</div>
      ) : data ? (
        <div className="space-y-5">
          {/* Header */}
          <div className="relative card-fah overflow-hidden">
            {data.seller.shop_banner ? <img src={data.seller.shop_banner} alt="banner" className="w-full h-32 object-cover" /> : <div className="w-full h-24 bg-gradient-to-r from-primary to-primary-hover" />}
            <div className="p-4 flex flex-wrap items-center gap-4">
              {(data.seller.shop_logo || data.seller.avatar) ? (
                <img src={data.seller.shop_logo || data.seller.avatar} alt="logo" className="w-16 h-16 rounded-lg object-cover -mt-12 border-4 border-white shadow" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center -mt-12 border-4 border-white shadow"><Store className="w-7 h-7 text-primary" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl font-bold">{data.seller.shop_name}</div>
                <div className="text-sm text-muted-foreground">{data.seller.owner_name}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${VERIFY_BADGE[data.seller.verified]}`}>{data.seller.verified ? "Verified" : "Pending"}</span>
                <span className={`text-xs font-semibold uppercase px-2 py-1 rounded ${STATUS_BADGE[data.seller.account_status] || STATUS_BADGE.active}`}>{data.seller.account_status || "active"}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Package} label="Products" value={data.stats.product_count} color="text-primary bg-primary/10" />
            <StatCard icon={IndianRupee} label="Revenue" value={formatINR(data.stats.revenue)} color="text-green-700 bg-green-100" />
            <StatCard icon={Star} label="Avg Rating" value={data.stats.avg_rating} color="text-amber-700 bg-amber-100" />
            <StatCard icon={Power} label="Orders" value={data.stats.order_count} color="text-purple-700 bg-purple-100" />
          </div>

          {/* Info grid */}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <InfoBlock title="Personal Information">
              <InfoRow icon={Phone} label="Mobile" value={data.seller.phone} />
              <InfoRow icon={Mail} label="Email" value={data.seller.email} />
              <InfoRow label="Aadhaar" value={data.seller.aadhaar_number || "—"} mono />
              <InfoRow label="PAN" value={data.seller.pan_number || "—"} mono />
            </InfoBlock>
            <InfoBlock title="Shop Information">
              <InfoRow icon={MapPin} label="Address" value={data.seller.address} />
              <InfoRow label="State / District" value={`${data.seller.state || "—"} · ${data.seller.district || data.seller.city || "—"}`} />
              <InfoRow label="Pincode" value={data.seller.pincode || "—"} />
              <InfoRow label="GST" value={data.seller.gst_number || "—"} mono />
              <InfoRow label="License" value={data.seller.license_number || "—"} mono />
            </InfoBlock>
            {(data.seller.bank_account_number || data.seller.bank_ifsc) && (
              <InfoBlock title="Bank Details" className="sm:col-span-2">
                <InfoRow label="Holder" value={data.seller.bank_holder_name || "—"} />
                <InfoRow label="Account No" value={data.seller.bank_account_number || "—"} mono />
                <InfoRow label="IFSC" value={data.seller.bank_ifsc || "—"} mono />
              </InfoBlock>
            )}
          </div>

          {/* Products list */}
          <div>
            <div className="text-sm font-display font-bold mb-2">Uploaded Products ({data.products.length})</div>
            <div className="card-fah overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>{["Product", "Category", "Price", "Stock", "Rating"].map((h) => <th key={h} className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.products.slice(0, 25).map((p) => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-3 py-2 flex items-center gap-2"><img src={p.images?.[0]} alt="" className="w-8 h-8 rounded object-cover" /><span className="line-clamp-1 max-w-[200px]">{p.name}</span></td>
                      <td className="px-3 py-2 capitalize">{(p.category || "").replace(/-/g, " ")}</td>
                      <td className="px-3 py-2 font-semibold">{formatINR(p.price)}</td>
                      <td className="px-3 py-2">{p.stock}</td>
                      <td className="px-3 py-2">⭐ {p.rating}</td>
                    </tr>
                  ))}
                  {data.products.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-muted-foreground">No products yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
};

// ---------- Tiny presentational helpers ----------
const ModalShell = ({ title, onClose, children, testid, wide = false }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
    <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-t-2xl sm:rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`}>
      <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
        <h2 className="font-display font-bold text-base sm:text-lg">{title}</h2>
        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded" aria-label="Close" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  </div>
);

const FormSection = ({ title, children }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-wide text-primary mb-2">{title}</div>
    {children}
  </div>
);
const FieldGrid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
const TextField = ({ label, value, onChange, type = "text", required = false, placeholder, className = "", testid }) => (
  <div className={className}>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid={testid} />
  </div>
);

const InfoBlock = ({ title, children, className = "" }) => (
  <div className={`card-fah p-4 ${className}`}>
    <div className="text-xs font-bold uppercase tracking-wide text-primary mb-2">{title}</div>
    <dl className="space-y-1.5">{children}</dl>
  </div>
);
const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />}
    <div className="text-xs text-muted-foreground w-24">{label}</div>
    <div className={`flex-1 ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</div>
  </div>
);
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="card-fah p-3 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  </div>
);

// Wrapper around ImageUploader to manage a SINGLE image (logo/banner)
const SingleImageUploader = ({ value, onChange, testid }) => {
  const list = value ? [value] : [];
  return (
    <div data-testid={testid}>
      <ImageUploader
        value={list}
        onChange={(arr) => onChange(arr[arr.length - 1] || "")}
        maxFiles={1}
      />
    </div>
  );
};

export default SellerManagement;
